import * as React from 'react'

import { Notification, Paragraph, useNotification } from '../components'
import { captureException } from './helpers'
import { useIsMounted } from './use-is-mounted'

const cache: any = {}
const errorsCache: any = {}

const NUM_OF_RETRIES = 3

enum ErrorSeverity {
  medium,
  high,
}

function importWithRetry<T = unknown>(importFn: () => Promise<T>, retries = 0) {
  const result = importFn().catch(async (err) => {
    if (retries > NUM_OF_RETRIES) {
      throw err
    }

    await new Promise((r) => setTimeout(r, 1000 * retries))

    return importWithRetry(importFn, retries + 1)
  }) as Promise<T>

  return result
}

/**
 * Hook used to asynchronously load a module
 *
 * Usage: [wiki](https://github.com/finvizhq/Finviz-Website/wiki/Loading-modules-asynchronously#useasyncmodule)
 */
export function useAsyncModule<T, SuspenseMode extends boolean>({
  importFn,
  onError,
  errorSeverity,
  cacheKey,
  shouldLoadModule = false,
  isSuspenseMode = false as SuspenseMode,
}: {
  importFn: () => Promise<T>
  onError?: () => void
  cacheKey: string
  errorSeverity?: keyof typeof ErrorSeverity
  shouldLoadModule?: boolean
  isSuspenseMode?: SuspenseMode
}) {
  const notificationContext = useNotification()
  const [isLoading, setIsLoading] = React.useState(false)
  const [isError, setIsError] = React.useState(false)
  const getIsMounted = useIsMounted()

  const handleError = () => {
    onError?.()
    setIsError(true)

    if (errorSeverity === undefined) {
      return
    }

    if (ErrorSeverity[errorSeverity] === ErrorSeverity.high) {
      window.handleScriptNotLoaded()
      window.renderScriptNotLoaded()
    }

    if (ErrorSeverity[errorSeverity] === ErrorSeverity.medium) {
      notificationContext.show(
        <Notification actions={[]} timeoutInMs={6000}>
          <Paragraph className="max-w-[17rem]">
            One or more of the required application components couldn’t be loaded. <br /> Please try to repeat the
            action or{' '}
            <span className="cursor-pointer underline" onClick={() => window.location.reload()}>
              refresh the page
            </span>{' '}
            if the problem persists.
          </Paragraph>
        </Notification>
      )
    }
  }

  const loadModule = async () => {
    if (cache[cacheKey]) {
      return cache[cacheKey]
    }

    setIsLoading(true)

    try {
      cache[cacheKey] = await importWithRetry(importFn)
    } catch (error) {
      captureException(error as Error)
      handleError()
    } finally {
      if (getIsMounted()) {
        setIsLoading(false)
      }
    }

    return cache[cacheKey]
  }

  React.useEffect(() => {
    if (shouldLoadModule) {
      loadModule()
    }
    // We want to run it only on shouldLoadModule change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadModule])

  const cachedModule = cache[cacheKey]
  if (isSuspenseMode) {
    if (errorsCache[cacheKey]) throw errorsCache[cacheKey]
    if (!cachedModule) {
      throw importFn()
        .then((mod) => (cache[cacheKey] = mod))
        .catch((err) => {
          handleError()
          errorsCache[cacheKey] = err
        })
    }
  }

  return [cachedModule ?? null, { isLoading, loadModule, isError }] as [
    SuspenseMode extends true ? T : T | null,
    { isLoading: boolean; loadModule: () => Promise<T>; isError: boolean },
  ]
}

/**
 * HOC which wraps component in React.Suspense
 *
 * Usage: [wiki](https://github.com/finvizhq/Finviz-Website/wiki/Loading-modules-asynchronously#withsuspense)
 */
export function withSuspense<P extends Record<string, any>>(
  Component: React.ComponentType<P>,
  fallback: React.ReactNode = null
) {
  return function WrappedComponent(props: P) {
    return (
      <React.Suspense fallback={fallback}>
        <Component {...props} />
      </React.Suspense>
    )
  }
}
